import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppUseDispatch, AppUseSelector } from '../../../redux/hooks'
import { SET_EXAM_BAND } from '../../../redux/types'
import { getPlacementExams } from '../../../redux/actions/onboardingAction'
import { trackOnboarding } from '../../../utils/analytics'
import { StepTitle, StepSubtitle } from '../stepContext'

interface ExamLevel {
  label: string
  vocab: number
  band_id: string
}

interface ExamSystem {
  system: string
  levels: ExamLevel[]
}

const ExamStep = ({ next }: { next: () => void }) => {
  const { t } = useTranslation('onboarding')
  const dispatch = AppUseDispatch()
  const { languageInfo, examBand } = AppUseSelector(({ onboarding }) => onboarding)
  const [systems, setSystems] = useState<ExamSystem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    if (!languageInfo?._id) {
      setLoading(false)
      return
    }
    getPlacementExams(languageInfo._id).then((data) => {
      if (!mounted) return
      setSystems(Array.isArray(data) ? data : [])
      setLoading(false)
    })
    return () => {
      mounted = false
    }
  }, [languageInfo?._id])

  const selectBand = (level: ExamLevel) => {
    dispatch({ type: SET_EXAM_BAND, payload: level })
    trackOnboarding('exam_level_selected', { exam_label: level.label, exam_vocab: level.vocab })
    next()
  }

  const skipExam = () => {
    dispatch({ type: SET_EXAM_BAND, payload: null })
    trackOnboarding('exam_level_selected', { exam_label: 'none' })
    next()
  }

  return (
    <div className='ob-step'>
      <StepTitle>{t('exam.title')}</StepTitle>
      <StepSubtitle>{t('exam.subtitle')}</StepSubtitle>

      {loading ? (
        <div className='ob-loader'>
          <div className='ob-spinner' />
        </div>
      ) : (
        <>
          {systems.map((system) => (
            <div key={system.system} className='ob-chip-group'>
              <p className='ob-chip-group-label'>{system.system}</p>
              <div className='ob-chips'>
                {system.levels.map((level) => (
                  <button
                    key={level.band_id + level.label}
                    type='button'
                    className={`ob-chip ${examBand?.label === level.label ? 'is-selected' : ''}`}
                    onClick={() => selectBand(level)}
                  >
                    {/* strip only the system qualifier, e.g. "(HSK 2.0)" — TOCFL's parenthetical IS the level */}
                    {level.label.replace(/\s*\(\s*(?:New\s+)?HSK[^)]*\)$/i, '')}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className='ob-actions'>
            <button type='button' className='ob-cta-ghost' onClick={skipExam}>
              {t('exam.skip')}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default ExamStep
